import { useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Mail, Plus, Trash2, X } from 'lucide-react'

import { getSessionEpoch } from '@/lib/auth'
import { useDebouncedValue } from '@/lib/use-debounced-value'
import { Button } from '@/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { Input } from '@/ui/input'
import { findAlertRecipients } from '../../api/alert-rules-api'
import type { EventRuleRecipient } from '../../api/event-rule-policies-api'

import {
  actionDraft,
  MAX_NOTIFICATION_RECIPIENTS,
  type EventActionDraft,
} from './event-action-model'

export function EventNotificationActions({
  entityId,
  value,
  onChange,
  disabled,
  canAddEmail,
}: {
  entityId: string
  value: EventActionDraft[]
  onChange: (actions: EventActionDraft[]) => void
  disabled: boolean
  canAddEmail: boolean
}) {
  const addAction = useRef<HTMLButtonElement>(null)
  return (
    <fieldset disabled={disabled} className="flex min-w-0 flex-col gap-3">
      <legend className="mb-2 text-xs font-medium">Then</legend>
      {value.map((action, index) => (
        <div key={action.key} className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2 rounded-md border p-2">
            <span className="flex items-center gap-2 text-xs">
              <Mail aria-hidden className="size-3.5" />
              Send notification
            </span>
            <EventRecipients
              entityId={entityId}
              value={action.recipients}
              disabled={disabled}
              canAddEmail={canAddEmail}
              onChange={(recipients) =>
                onChange(
                  value.map((item, position) =>
                    position === index ? { ...item, recipients } : item,
                  ),
                )
              }
            />
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            disabled={disabled}
            aria-label={`Remove notification action ${index + 1}`}
            onClick={() => {
              onChange(value.filter((item) => item.key !== action.key))
              addAction.current?.focus()
            }}
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      ))}
      <div>
        <DropdownMenu>
          <DropdownMenuTrigger
            ref={addAction}
            disabled={disabled}
            render={<Button type="button" variant="outline" size="sm" />}
          >
            <Plus aria-hidden />
            Add action
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onClick={() =>
                onChange([
                  ...value,
                  actionDraft({
                    type: 'notification',
                    recipients: [],
                  }),
                ])
              }
            >
              <Mail aria-hidden className="size-3.5" />
              Send notification
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className="text-muted-foreground text-[11px]">
        Choose who should receive notifications for this rule.
      </p>
    </fieldset>
  )
}

function recipientKey(recipient: EventRuleRecipient) {
  return `${recipient.type}:${recipient.id ?? recipient.address}`
}

function EventRecipients({
  entityId,
  value,
  onChange,
  disabled,
  canAddEmail,
}: {
  entityId: string
  value: EventRuleRecipient[]
  onChange: (recipients: EventRuleRecipient[]) => void
  disabled: boolean
  canAddEmail: boolean
}) {
  const inputId = useId()
  const searchInput = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState('')
  const debounced = useDebouncedValue(search, 250)
  const candidates = useQuery({
    queryKey: [
      'assets',
      'event-rule-recipients',
      getSessionEpoch(),
      entityId,
      debounced,
    ],
    queryFn: async ({ signal }) =>
      (
        await Promise.all(
          (['user', 'group'] satisfies ('user' | 'group')[]).map((kind) =>
            findAlertRecipients(entityId, kind, debounced, signal),
          ),
        )
      ).flat(),
    enabled: !disabled,
  })
  const [names, setNames] = useState<{
    data: typeof candidates.data
    labels: Record<string, string>
  }>({ data: undefined, labels: {} })
  if (candidates.data && candidates.data !== names.data) {
    setNames({
      data: candidates.data,
      labels: {
        ...names.labels,
        ...Object.fromEntries(
          candidates.data.map((item) => [`${item.type}:${item.id}`, item.name]),
        ),
      },
    })
  }
  const available =
    candidates.data?.filter(
      (candidate) =>
        !value.some(
          (recipient) =>
            recipient.type === candidate.type && recipient.id === candidate.id,
        ),
    ) ?? []
  const email = search.trim()
  const validEmail =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254
  const duplicateEmail = value.some(
    (item) =>
      item.type === 'email' &&
      item.address?.toLowerCase() === email.toLowerCase(),
  )
  const full = value.length >= MAX_NOTIFICATION_RECIPIENTS
  function add(recipient: EventRuleRecipient) {
    if (disabled || full) return
    onChange([...value, recipient])
    setSearch('')
  }
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <label htmlFor={inputId} className="sr-only">
        Notification recipients
      </label>
      <ul className="flex flex-wrap gap-1" aria-label="Selected recipients">
        {value.map((recipient) => {
          const label =
            recipient.type === 'email'
              ? recipient.address
              : (names.labels[recipientKey(recipient)] ??
                `${recipient.type === 'group' ? 'Group' : 'User'} ${recipient.id ?? '(unavailable)'}`)
          return (
            <li
              key={recipientKey(recipient)}
              className="flex min-w-0 max-w-full items-center gap-1 rounded bg-muted py-0.5 pl-2 text-xs"
            >
              <span className="min-w-0 truncate" title={label ?? undefined}>
                {label}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="shrink-0"
                disabled={disabled}
                aria-label={`Remove ${label}`}
                onClick={() => {
                  onChange(
                    value.filter(
                      (item) => recipientKey(item) !== recipientKey(recipient),
                    ),
                  )
                  searchInput.current?.focus()
                }}
              >
                <X aria-hidden />
              </Button>
            </li>
          )
        })}
      </ul>
      <Input
        ref={searchInput}
        id={inputId}
        value={search}
        disabled={disabled || full}
        maxLength={254}
        placeholder={
          canAddEmail ? 'Type a name or email…' : 'Search users and groups…'
        }
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return
          event.preventDefault()
          if (canAddEmail && validEmail && !duplicateEmail)
            add({ type: 'email', address: email })
        }}
      />
      {search && (
        <div
          className="max-h-40 min-w-0 max-w-full overflow-y-auto rounded-md border p-1"
          aria-label="Recipient suggestions"
        >
          {candidates.isFetching && (
            <p role="status" className="p-1 text-xs text-muted-foreground">
              Searching recipients…
            </p>
          )}
          {candidates.isError && (
            <p role="alert" className="text-xs">
              Recipients could not be loaded.{' '}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => void candidates.refetch()}
              >
                Retry
              </Button>
            </p>
          )}
          {available.map((candidate) => (
            <Button
              key={`${candidate.type}:${candidate.id}`}
              type="button"
              variant="ghost"
              className="w-full min-w-0 justify-start"
              disabled={disabled || full || search !== debounced}
              onClick={() => add({ type: candidate.type, id: candidate.id })}
            >
              <span className="min-w-0 truncate" title={candidate.name}>
                {candidate.name}
              </span>
              <span className="shrink-0 text-muted-foreground">
                ({candidate.type})
              </span>
            </Button>
          ))}
          {canAddEmail && validEmail && !duplicateEmail && (
            <Button
              type="button"
              variant="ghost"
              className="w-full min-w-0 justify-start"
              disabled={disabled || full}
              onClick={() => add({ type: 'email', address: email })}
            >
              <span className="min-w-0 truncate" title={email}>
                Add {email}
              </span>
            </Button>
          )}
          {!candidates.isFetching &&
            !candidates.isError &&
            available.length === 0 &&
            !validEmail && (
              <p className="p-1 text-xs text-muted-foreground">
                No matching users or groups.
              </p>
            )}
          {duplicateEmail && (
            <p className="p-1 text-xs text-muted-foreground">
              This email is already selected.
            </p>
          )}
        </div>
      )}
      <span className="text-[11px] text-muted-foreground">
        {value.length} of {MAX_NOTIFICATION_RECIPIENTS} recipients
        {!canAddEmail &&
          '. Adding email addresses requires email-recipient permission.'}
      </span>
    </div>
  )
}
